import { FormEvent, useEffect, useState } from 'react';
import InputField from '../../../components/Forms/Input/InputField';
import SelectField from '../../../components/Forms/Input/SelectField';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import { useBranch } from '../../../hooks/branches/useBranch';
import { useDteBulkDownload } from '../../../hooks/reports/useDteBulkDownload';
import useConfirm from '../../../hooks/useConfirmModal';
import {
  DteBulkFileType,
  DteBulkModule,
  DteDocumentStatus,
  SaleDteBulkDownloadRequest,
} from '../../../types/reports/dteBulkDownload/SaleDteBulkDownloadRequest';
import DatePicker from '../../../components/Forms/DatePicker/DatePicker';

type FormState = {
  module: DteBulkModule;
  branchId: number | null;
  documentStatus: DteDocumentStatus | '';
  dateFrom: string;
  dateTo: string;
  fileType: DteBulkFileType | '';
  zipName: string;
};

const initialState: FormState = {
  module: 'SALES',
  branchId: null,
  documentStatus: '',
  dateFrom: '',
  dateTo: '',
  fileType: '',
  zipName: '',
};

export const normalizeZipName = (value: string) =>
  value.trim().replace(/\.zip$/i, '');

export const validateDteBulkDownload = (form: FormState) => {
  const errors: Partial<Record<keyof FormState, string>> = {};
  if (!form.module) errors.module = 'Seleccione un módulo.';
  if (!form.branchId) errors.branchId = 'Seleccione una sucursal.';
  if (!form.documentStatus) errors.documentStatus = 'Seleccione un estado.';
  if (!form.dateFrom) errors.dateFrom = 'Ingrese la fecha inicial.';
  if (!form.dateTo) errors.dateTo = 'Ingrese la fecha final.';
  if (form.dateFrom && form.dateTo && form.dateFrom > form.dateTo) {
    errors.dateTo = 'La fecha final debe ser igual o posterior a la inicial.';
  }
  if (!form.fileType) errors.fileType = 'Seleccione el tipo de archivo.';

  const normalizedName = normalizeZipName(form.zipName);
  if (!normalizedName)
    errors.zipName = 'Ingrese un nombre para el archivo ZIP.';
  else if (/[/\\]|\.\.|[\u0000-\u001f\u007f]/.test(normalizedName)) {
    errors.zipName =
      'El nombre no puede contener /, \\, .. ni caracteres de control.';
  }
  return errors;
};

const DteBulkDownloadForm = () => {
  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormState, string>>
  >({});
  const { branchDropdown, fetchSalesBranchDropdown } = useBranch();
  const { download, isDownloading } = useDteBulkDownload();
  const confirm = useConfirm();

  useEffect(() => {
    fetchSalesBranchDropdown();
  }, []);

  const update = (name: keyof FormState, value: FormState[keyof FormState]) => {
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const nextErrors = validateDteBulkDownload(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length || isDownloading) return;

    const payload: SaleDteBulkDownloadRequest = {
      module: form.module,
      branchId: form.branchId!,
      documentStatus: form.documentStatus as DteDocumentStatus,
      dateFrom: form.dateFrom,
      dateTo: form.dateTo,
      fileType: form.fileType as DteBulkFileType,
      zipName: normalizeZipName(form.zipName),
    };

    confirm({
      title: 'Preparar descarga masiva',
      message:
        'El sistema verificará el tamaño de la descarga. Si es pequeña, se descargará de inmediato; si es grande, se generará en segundo plano.',
      icon: 'info',
      confirmText: 'Continuar',
      cancelText: 'Cancelar',
      onConfirm: () => download(payload),
    });
  };

  return (
    <form onSubmit={submit} className="p-6.5">
      <div className="mb-6">
        <p className="mt-1 text-sm text-body dark:text-bodydark2">
          Seleccione los documentos y formatos que desea incluir en el archivo
          ZIP.
        </p>
      </div>

      <div className="mb-4">
        <SelectField
          label="Módulo"
          name="module"
          value={form.module}
          options={[
            { value: 'SALES', label: 'Ventas' },
            { value: 'PURCHASES', label: 'Compras' },
          ]}
          onChange={(event) =>
            update('module', event.target.value as DteBulkModule)
          }
          error={errors.module}
          required
          disabled={isDownloading}
          iconKey="layers"
        />
        {form.module === 'PURCHASES' && (
          <p className="mt-1 text-sm text-body dark:text-bodydark2">
            En compras solo se incluyen las facturas de sujeto excluido.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2">
        <SimpleSearchSelect
          label="Sucursal"
          name="branchId"
          options={branchDropdown ?? []}
          selectedValue={form.branchId}
          onSelect={(option) => update('branchId', option.id)}
          error={errors.branchId}
          required
          disabled={isDownloading}
          iconKey="branch"
        />
        <SelectField
          label="Estado"
          name="documentStatus"
          value={form.documentStatus}
          options={[
            { value: 'FINALIZED', label: 'Finalizados' },
            { value: 'CANCELED', label: 'Anulados' },
          ]}
          onChange={(event) =>
            update('documentStatus', event.target.value as DteDocumentStatus)
          }
          error={errors.documentStatus}
          required
          disabled={isDownloading}
          iconKey="status"
        />
        <DatePicker
          label="Fecha inicial"
          name="dateFrom"
          value={form.dateFrom}
          onChange={(date) => update('dateFrom', date)}
          error={errors.dateFrom}
          required
          disabled={isDownloading}
          iconKey="calendar"
        />

        <DatePicker
          label="Fecha final"
          name="dateTo"
          value={form.dateTo}
          onChange={(date) => update('dateTo', date)}
          error={errors.dateTo}
          required
          disabled={isDownloading}
          iconKey="calendar"
        />
        <SelectField
          label="Tipo de archivo"
          name="fileType"
          value={form.fileType}
          options={[
            { value: 'PDF', label: 'Solo PDF' },
            { value: 'JSON', label: 'Solo JSON' },
            { value: 'PDF_AND_JSON', label: 'PDF y JSON' },
          ]}
          onChange={(event) =>
            update('fileType', event.target.value as DteBulkFileType)
          }
          error={errors.fileType}
          required
          disabled={isDownloading}
          iconKey="file"
        />
        <InputField
          label="Nombre del archivo ZIP"
          name="zipName"
          type="text"
          value={form.zipName}
          placeholder="Ej. documentos-dte-julio"
          onChange={(event) => update('zipName', event.target.value)}
          onBlur={() => update('zipName', normalizeZipName(form.zipName))}
          error={errors.zipName}
          required
          disabled={isDownloading}
          iconKey="archive"
        />
      </div>

      <div className="mt-2 flex justify-end border-t border-stroke pt-6 dark:border-strokedark">
        <button
          type="submit"
          disabled={isDownloading}
          className="inline-flex items-center rounded bg-primary px-6 py-3 font-medium text-white hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isDownloading ? 'Preparando...' : 'Preparar descarga'}
        </button>
      </div>
    </form>
  );
};

export default DteBulkDownloadForm;
